<template>
  <Dialog :open="modelValue" @update:open="onOpenChange">
    <DialogContent
      class="max-w-[800px] gap-0 overflow-hidden p-0 sm:max-w-[800px]"
      :show-close-button="false"
      :aria-describedby="undefined"
      @escape-key-down="onEscapeKeyDown"
      @interact-outside="onInteractOutside"
    >
      <!--
        The visible step-1 heading is not rendered on steps 2/3, so reka-ui's
        required title is provided screen-reader-only and kept stable across
        steps. There is no single sentence that describes all three steps, so
        the description is opted out of explicitly (same approach as HubDialog).
      -->
      <DialogTitle class="sr-only">{{ t('nps.title') }}</DialogTitle>

      <!-- Confetti for promoter OMR step -->
      <div
        v-if="currentStep === 3 && showOmrCta"
        class="nps-confetti"
        aria-hidden="true"
      >
        <i
          v-for="(piece, i) in confettiPieces"
          :key="i"
          :style="{
            left: `${piece.left}%`,
            background: piece.hue,
            animationDelay: `${piece.delay}s`,
            '--dx': `${piece.dx}px`,
          }"
        />
      </div>

      <!-- Progress indicator -->
      <div v-if="isSubmitting" class="absolute left-0 top-0 z-50 w-full">
        <Progress indeterminate class="h-1 rounded-none" />
      </div>

      <!-- Close button -->
      <Button
        v-if="!isSubmitting"
        variant="ghost"
        size="icon"
        class="absolute right-4 top-4 z-10"
        :aria-label="t('common.close')"
        @click="handleClose"
      >
        <X class="text-primary/60" />
      </Button>

      <!-- Card content -->
      <div class="px-6 pb-4 pt-8">
        <!-- Step indicator (shared across all steps) -->
        <div class="mb-[18px] flex justify-center gap-1.5">
          <span
            v-for="n in 3"
            :key="n"
            class="nps-dot"
            :class="{ on: n <= currentStep }"
          />
        </div>

        <!-- Step 1: Score Selection -->
        <div v-if="currentStep === 1">
          <!-- Question Title -->
          <h2 class="nps-question mb-6 text-balance px-8 text-center">
            {{ t('nps.title') }}
          </h2>

          <!-- NPS Scale 0-10 (Scrollable with indicators) -->
          <div class="nps-scale-section">
            <div ref="scaleWrapper" class="nps-scale-wrapper">
              <div v-if="showLeftIndicator" class="scroll-indicator scroll-indicator-left">
                <ChevronLeft class="size-4" />
              </div>
              <div class="nps-scale-container">
                <div class="nps-pill-scale" role="radiogroup" aria-label="NPS scale">
                  <button
                    v-for="score in 11"
                    :key="score - 1"
                    type="button"
                    role="radio"
                    :aria-checked="selectedScore === score - 1"
                    class="nps-pill focus-visible:ring-ring/50 focus-visible:outline-hidden focus-visible:ring-[3px]"
                    :class="{ 'nps-pill--selected': selectedScore === score - 1 }"
                    data-cy="nps-score-btn"
                    @click="selectedScore = score - 1"
                  >
                    {{ score - 1 }}
                  </button>
                </div>
              </div>
              <div v-if="showRightIndicator" class="scroll-indicator scroll-indicator-right">
                <ChevronRight class="size-4" />
              </div>
            </div>

            <!-- Helper labels with emoji anchors -->
            <div class="mt-3 flex justify-between px-2">
              <span class="nps-anchor">
                <span class="nps-anchor-emoji">😕</span>
                <span>{{ t('nps.scaleLabel.min') }}</span>
              </span>
              <span class="nps-anchor text-right">
                <span class="nps-anchor-emoji">🤩</span>
                <span>{{ t('nps.scaleLabel.max') }}</span>
              </span>
            </div>
          </div>

          <!-- "Not at the moment" link -->
          <div class="mt-6 text-center">
            <button
              type="button"
              class="text-muted-foreground cursor-pointer text-xs underline hover:opacity-80"
              @click="handleIgnore"
            >
              {{ t('nps.actions.notAtTheMoment') }}
            </button>
          </div>
        </div>

        <!-- Step 2: Optional Feedback -->
        <div v-if="currentStep === 2">
          <p class="nps-step2-heading mb-4 px-4 text-center">
            {{ step2Prompt }}
            <span class="text-muted-foreground font-normal">{{ t('nps.step2.subtitle') }}</span>
          </p>
          <div
            v-if="topicOptions.length"
            class="mb-4 flex flex-wrap justify-center gap-2 px-4"
            role="group"
            aria-label="Topic chips"
          >
            <button
              v-for="topic in topicOptions"
              :key="topic"
              type="button"
              class="nps-chip focus-visible:ring-ring/50 focus-visible:outline-hidden focus-visible:ring-[3px]"
              :class="{ 'nps-chip--selected': selectedTopics.includes(topic) }"
              :aria-pressed="selectedTopics.includes(topic)"
              data-cy="nps-topic-chip"
              @click="toggleTopic(topic)"
            >
              {{ topic }}
            </button>
          </div>
          <div class="px-4">
            <Textarea
              v-model="feedback"
              :placeholder="t('nps.step2.placeholder')"
              rows="4"
              maxlength="5000"
              data-cy="nps-feedback-textarea"
            />
            <div class="text-muted-foreground mt-1 text-right text-xs">
              {{ feedback.length }} / 5000
            </div>
          </div>
        </div>

        <!-- Step 3: End Screen -->
        <div v-if="currentStep === 3" class="flex min-h-[140px] items-center justify-center">
          <div v-if="!showOmrCta" class="text-center">
            <p
              v-for="(segment, index) in thanksSegments"
              :key="`thanks-${index}`"
              :class="[
                index === thanksSegments.length - 1 ? 'mb-0' : 'mb-2',
                index === 0 ? 'text-xl font-medium' : 'text-base',
              ]"
            >
              {{ segment }}
            </p>
          </div>

          <div v-else>
            <h2 class="px-4 text-xl font-medium">{{ omrCtaSegments[0] }}</h2>
            <div class="omr-review-cta">
              <img :src="omrLogoUrl" alt="OMR logo" class="w-12" />
              <p class="mb-0 text-base">{{ omrCtaSegments[1] }}</p>
            </div>

            <p class="mb-0 px-4 text-base">{{ omrCtaSegments[2] }}</p>
          </div>
        </div>

        <!-- Error message -->
        <Alert v-if="errorMessage" variant="destructive" class="mx-4 mt-4 w-auto">
          <AlertDescription>{{ errorMessage }}</AlertDescription>
        </Alert>
      </div>

      <!-- Actions -->
      <template v-if="showActions">
        <Separator />
        <div class="flex items-center justify-between p-4">
          <Button
            variant="outline"
            :disabled="isSubmitting"
            @click="handleSecondary"
          >
            {{ secondaryLabel }}
          </Button>
          <Button
            :disabled="isPrimaryDisabled || isSubmitting"
            data-cy="nps-primary-btn"
            @click="handlePrimary"
          >
            {{ primaryLabel }}
          </Button>
        </div>
      </template>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronLeft, ChevronRight, X } from '@lucide/vue';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import NpsService from '@/services/nps';
import type { NpsOmrCtaAction, NpsSubmitPayload } from '@/services/nps';
import omrLogoUrl from '@/assets/brands/OMR_filled.svg';

/** A single falling confetti particle on the OMR end screen. */
interface ConfettiPiece {
  /** Horizontal start position, in % of the dialog width. */
  left: number;
  /** Horizontal drift over the fall, in px. */
  dx: number;
  /** Animation start delay, in seconds. */
  delay: number;
  /** Particle colour. */
  hue: string;
}

/** Which bucket the chosen score falls into — drives copy and topic chips. */
type ScoreSegment = 'detractor' | 'passive' | 'promoter';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const { t, tm, rt } = useI18n();

const OMR_REVIEW_URL = 'https://omr.com/de/reviews/new/product/moinai';

/**
 * Scores at or above this get the OMR review call-to-action. Deliberately one
 * lower than the `promoter` segment cut-off (9) — a "passive" 8 is still happy
 * enough to be worth asking for a public review.
 */
const OMR_CTA_MIN_SCORE = 8;

const currentStep = ref(1);
const selectedScore = ref<number | null>(null);
const feedback = ref('');
const isSubmitting = ref(false);
const errorMessage = ref('');
const showLeftIndicator = ref(false);
const showRightIndicator = ref(false);
const showOmrCta = ref(false);
const autoCloseTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const confettiPieces = ref<ConfettiPiece[]>([]);
const selectedTopics = ref<string[]>([]);

const scaleWrapper = useTemplateRef<HTMLDivElement>('scaleWrapper');

const primaryLabel = computed(() => {
  if (currentStep.value === 1) return t('nps.step1.primaryButton');
  if (currentStep.value === 3) return t('nps.step3.primaryButton');
  return t('nps.step2.primaryButton');
});

const secondaryLabel = computed(() => {
  if (currentStep.value === 1) return t('nps.actions.notNow');
  if (currentStep.value === 2) return t('nps.step2.secondaryButton');
  if (currentStep.value === 3) return t('nps.step3.secondaryButton');
  return '';
});

const isPrimaryDisabled = computed(() => currentStep.value === 1 && selectedScore.value === null);

const showActions = computed(() => currentStep.value !== 3 || showOmrCta.value);

/**
 * `tm()` (not `t()`) is required for array-valued messages in vue-i18n's
 * Composition mode; each entry then has to go through `rt()` to be resolved.
 */
const translatedList = (key: string): string[] => {
  const entries = tm(key) as unknown;
  return Array.isArray(entries) ? entries.map((entry) => rt(entry as any)) : [];
};

const thanksSegments = computed(() => translatedList('nps.step3.thanksSegments'));
const omrCtaSegments = computed(() => translatedList('nps.step3.omrCtaSegments'));

const scoreSegment = computed<ScoreSegment>(() => {
  // Matches the legacy behaviour where an unset score reads as a detractor;
  // only ever consumed on step 2, once a score has actually been picked.
  const score = selectedScore.value ?? 0;
  if (score <= 6) return 'detractor';
  if (score <= 8) return 'passive';
  return 'promoter';
});

const step2Prompt = computed(() => t(`nps.step2.prompts.${scoreSegment.value}`));
const topicOptions = computed(() => translatedList(`nps.step2.topics.${scoreSegment.value}`));

const clearAutoCloseTimeout = () => {
  if (autoCloseTimeout.value) {
    clearTimeout(autoCloseTimeout.value);
    autoCloseTimeout.value = null;
  }
};

const generateConfettiPieces = (): ConfettiPiece[] => {
  const palette = ['#222C50', '#E8D9B8', '#00C28A', '#FBBA04', '#E45966'];
  return Array.from({ length: 24 }, () => ({
    left: Math.random() * 100,
    dx: (Math.random() - 0.5) * 200,
    delay: Math.random() * 0.4,
    hue: palette[Math.floor(Math.random() * palette.length)],
  }));
};

const checkScrollIndicators = () => {
  const wrapper = scaleWrapper.value;
  if (!wrapper) return;

  const hasScroll = wrapper.scrollWidth > wrapper.clientWidth;
  if (!hasScroll) {
    showLeftIndicator.value = false;
    showRightIndicator.value = false;
    return;
  }

  showLeftIndicator.value = wrapper.scrollLeft > 5;
  showRightIndicator.value = wrapper.scrollLeft < wrapper.scrollWidth - wrapper.clientWidth - 5;
};

const addScrollListener = () => {
  scaleWrapper.value?.addEventListener('scroll', checkScrollIndicators);
};

const removeScrollListener = () => {
  scaleWrapper.value?.removeEventListener('scroll', checkScrollIndicators);
};

const resetModal = () => {
  clearAutoCloseTimeout();
  currentStep.value = 1;
  selectedScore.value = null;
  feedback.value = '';
  isSubmitting.value = false;
  errorMessage.value = '';
  showOmrCta.value = false;
  confettiPieces.value = [];
  selectedTopics.value = [];
  removeScrollListener();
};

const closeModal = () => {
  emit('update:modelValue', false);
  nextTick(() => resetModal());
};

const buildSubmitPayload = ({
  includeFeedback = true,
  omrCtaAction = null,
}: { includeFeedback?: boolean; omrCtaAction?: NpsOmrCtaAction | null } = {}): NpsSubmitPayload => {
  const payload: NpsSubmitPayload = {
    action: 'SUBMIT',
    score: selectedScore.value ?? undefined,
  };

  if (includeFeedback && feedback.value.trim()) {
    payload.feedback = feedback.value.trim();
  }

  if (selectedTopics.value.length) {
    payload.topics = [...selectedTopics.value];
  }

  if (omrCtaAction) {
    payload.omrCtaAction = omrCtaAction;
  }

  return payload;
};

const submitNps = async ({
  includeFeedback = true,
  omrCtaAction = null,
  showThankYouAfterSubmit = true,
}: {
  includeFeedback?: boolean;
  omrCtaAction?: NpsOmrCtaAction | null;
  showThankYouAfterSubmit?: boolean;
} = {}) => {
  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    const wantsOmrCta = (selectedScore.value ?? 0) >= OMR_CTA_MIN_SCORE;

    // First pass for a high scorer: don't submit yet, show the OMR CTA and wait
    // for them to accept or skip it, so the choice rides along with the answer.
    if (wantsOmrCta && !omrCtaAction) {
      isSubmitting.value = false;
      errorMessage.value = '';
      currentStep.value = 3;
      showOmrCta.value = true;
      return;
    }

    await NpsService.submitResponse(buildSubmitPayload({ includeFeedback, omrCtaAction }));

    isSubmitting.value = false;
    errorMessage.value = '';

    if (!showThankYouAfterSubmit) {
      closeModal();
      return;
    }

    currentStep.value = 3;
    showOmrCta.value = false;

    clearAutoCloseTimeout();
    autoCloseTimeout.value = setTimeout(() => {
      closeModal();
    }, 3_000);
  } catch {
    errorMessage.value = t('nps.error');
    isSubmitting.value = false;
  }
};

const handleLater = async (trigger: string = 'not_now') => {
  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    await NpsService.submitResponse({ action: 'LATER', trigger });
    closeModal();
  } catch {
    errorMessage.value = t('nps.error');
    isSubmitting.value = false;
  }
};

const handleIgnore = async () => {
  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    await NpsService.submitResponse({ action: 'IGNORE' });
    closeModal();
  } catch {
    errorMessage.value = t('nps.error');
    isSubmitting.value = false;
  }
};

const handleOmrSkip = async () => {
  await submitNps({ omrCtaAction: 'skipped', showThankYouAfterSubmit: false });
};

const handleOmrRating = async () => {
  await submitNps({ omrCtaAction: 'clicked', showThankYouAfterSubmit: false });

  if (errorMessage.value) return;

  window.open(OMR_REVIEW_URL, '_blank', 'noopener,noreferrer');
};

const toggleTopic = (topic: string) => {
  const idx = selectedTopics.value.indexOf(topic);
  if (idx === -1) {
    selectedTopics.value.push(topic);
  } else {
    selectedTopics.value.splice(idx, 1);
  }
};

const handlePrimary = async () => {
  if (currentStep.value === 1) {
    // Move to step 2 (no loading state, no API call)
    currentStep.value = 2;
    removeScrollListener();
  } else if (currentStep.value === 2) {
    // Submit with or without feedback
    await submitNps();
  } else {
    await handleOmrRating();
  }
};

const handleSecondary = async () => {
  if (currentStep.value === 1) {
    // "Not now" - defer for 1 week
    await handleLater('not_now');
  } else if (currentStep.value === 2) {
    // "Skip" - submit score without feedback text
    await submitNps({ includeFeedback: false });
  } else {
    await handleOmrSkip();
  }
};

const handleClose = async () => {
  if (isSubmitting.value) return;

  if (currentStep.value === 3) {
    if (showOmrCta.value) {
      await handleOmrSkip();
      return;
    }

    closeModal();
    return;
  }

  await handleLater('x');
};

/**
 * The dialog is "persistent": reka must never close it on its own, because
 * every dismissal has to be reported to the backend first (so it knows whether
 * to ask again later). All of these funnel into the handlers below.
 */
const onInteractOutside = async (event: Event) => {
  event.preventDefault();
  await handleClose();
};

const onEscapeKeyDown = async (event: Event) => {
  event.preventDefault();
  if (isSubmitting.value || currentStep.value === 3) return;
  await handleLater('esc');
};

const onOpenChange = (open: boolean) => {
  if (open) {
    emit('update:modelValue', true);
    return;
  }
  closeModal();
};

/** Tell the backend the survey was actually put in front of the user. */
const handleMarkSeen = async () => {
  try {
    await NpsService.submitResponse({ action: 'SHOWN' });
  } catch (error) {
    // Fail silently - marking as seen is not critical
    console.error('Failed to mark NPS as seen:', error);
  }
};

watch(showOmrCta, (val) => {
  confettiPieces.value = val ? generateConfettiPieces() : [];
});

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      // Call this in the background
      handleMarkSeen();
    }

    if (val && currentStep.value === 1) {
      nextTick(() => {
        checkScrollIndicators();
        addScrollListener();
      });
    } else {
      removeScrollListener();
    }
  },
);

onBeforeUnmount(() => {
  removeScrollListener();
  clearAutoCloseTimeout();
});
</script>

<style scoped>
.nps-confetti {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 1;
}

.nps-confetti i {
  position: absolute;
  top: -20px;
  width: 8px;
  height: 12px;
  border-radius: 2px;
  opacity: 0.95;
  animation: nps-confetti-fall 1.6s linear forwards;
}

@keyframes nps-confetti-fall {
  0% {
    transform: translate(0, -20px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--dx), 480px) rotate(720deg);
    opacity: 0;
  }
}

.nps-dot {
  width: 24px;
  height: 4px;
  border-radius: 2px;
  background: var(--muted);
  transition: background 0.25s;
}

.nps-dot.on {
  background: var(--primary);
}

.nps-question {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--primary);
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

.nps-pill-scale {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
  padding: 6px 0;
}

.nps-pill {
  width: 48px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
  color: var(--foreground);
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    box-shadow 0.2s;
}

.nps-pill:hover {
  background: var(--accent);
  border-color: var(--ring);
  transform: translateY(-1px);
}

.nps-pill--selected,
.nps-pill--selected:hover {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--primary-foreground);
  transform: translateY(-2px);
}

.nps-pill--selected {
  box-shadow: 0 6px 18px -6px var(--primary);
}

.nps-anchor {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 500;
}

.nps-anchor-emoji {
  font-size: 18px;
  line-height: 1;
}

.nps-step2-heading {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary);
}

.nps-chip {
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: var(--muted-foreground);
  background: var(--background);
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;
}

.nps-chip:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--accent);
}

.nps-chip--selected,
.nps-chip--selected:hover {
  background: var(--primary);
  border-color: var(--primary);
  color: var(--primary-foreground);
}

.nps-scale-section {
  position: relative;
  padding: 0 16px;
  width: fit-content;
  margin: 0 auto;
  max-width: 100%;
}

.nps-scale-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  position: relative;
}

.nps-scale-wrapper::-webkit-scrollbar {
  height: 6px;
}

.nps-scale-wrapper::-webkit-scrollbar-track {
  background: var(--muted);
  border-radius: 3px;
}

.nps-scale-wrapper::-webkit-scrollbar-thumb {
  background: var(--muted-foreground);
  border-radius: 3px;
}

.scroll-indicator {
  position: absolute;
  top: 0;
  bottom: 6px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
  color: var(--muted-foreground);
}

.scroll-indicator-left {
  left: 0;
  background: linear-gradient(to right, var(--background), transparent);
}

.scroll-indicator-right {
  right: 0;
  background: linear-gradient(to left, var(--background), transparent);
}

.nps-scale-container {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  min-width: min-content;
}

.omr-review-cta {
  padding: 1rem;
  border-radius: 0.5rem;
  background: var(--muted);
  align-items: center;
  display: flex;
  gap: 1rem;
  margin: 0.5rem 0;
}

/* Responsive adjustments */
@media (max-width: 700px) {
  .nps-pill {
    width: 44px;
    height: 44px;
    font-size: 14px;
  }
}

@media (max-width: 500px) {
  .nps-pill {
    width: 38px;
    height: 38px;
  }
}
</style>
